import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import mapboxgl from 'mapbox-gl';
import mapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import "mapbox-gl-style-switcher/styles.css";
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import RulerControl from '@mapbox-controls/ruler';
import '@mapbox-controls/ruler/src/index.css';


import {ViewerContext} from "../context/ViewerContext";
import {ReviewContext} from "../context/ReviewContext";
import { UserContext } from '../context/UserContext';
import {getMap} from './utils/mapboxConfig';
import {handleMapInteraction, handleMapSelectChange, toggle3d, handleDrawCreate, handleDrawUpdate, handleDrawDelete, handleDrawModeChange,} from './utils/mapHandlers';
import {get3dStyle,getMainStyle} from './utils/mapStyles';
import {APIGetOrderReviews } from '../api/orders/OrdersReviews';
import {APIGetOpenDataPlan, APIGetOrderPlan, APIGetPlan, APIGetPlanIntegration} from "../api/plans/Plans";
import BaseMapSideOver from './components/baseMapSideOver';
import DetailsSideOver from './components/detailsSideOver';
import LayersSideOver from './components/layersSideOver';
import ReviewCreate from '../components/dialogs/reviewCreate';
import ReviewDetails from '../components/dialogs/reviewDetails';
import {ButtonPlanToOrder, ButtonLayerPicker, Button3D, ShowBaseMapPicker} from './components/mapButtons';
import {NewOrderModal} from '../components/dialogs/newOrderModal';
import { LoadingIndicator } from '../components/loadingIndicator/loadingIndicator';
import {HiChevronLeft} from "react-icons/hi";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

export const XPlanViewer = () => {

    const queryParameters = new URLSearchParams(window.location.search);
    const isReview = queryParameters.get('rId') === "1" ? true : false;

    const {id, mapsId, integrationAccessId, integrationPlanId,openDataPlanId} = useParams();
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const mapContainerRef = useRef(null);
    const [map, setMap] = useState(null);
    const [planName, setPlanName] = useState(null);
    const [legalShort, setLegalShort] = useState(null);
    const [planId, setPlanId] = useState(null);
    const [plan, setPlan] = useState(null);
    const [review, setReview] = useState(null);
    const [currentPoint,setCurrentPoint] = useState({});
    const [detailsData, setDetailsData] = useState([]);
    const [isIntegration, setIsIntegration] = useState(false);
    const [layerCategory, setLayerCategory] = useState(false);
    const [openReviewCreate,setOpenReviewCreate] = useState(false);
    const [openReviewDetails,setOpenReviewDetails] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [newOrder, setNewOrder] = useState(false);
    const [showAllLayer, setShowAllLayer] = useState(true);
    const [showBaseMap, setShowBaseMap] = useState(false);
    const [showLayer, setShowLayer] = useState(false);
    const [layerBlink, setLayerBlink] = useState(false);
    const [refs, setRefs] = useState([]);
    const [showConfigStyles, setShowConfigStyles] = useState(false);

    const fileName = queryParameters.get('f');
    const companyId = queryParameters.get('cId');
    const version = queryParameters.get('vId');
    const orderId = queryParameters.get('oId');
    const fileId = queryParameters.get('fId');
    const commentData = false;

    const getBackLink = () => {
        if (isReview) {
            return `/order/${orderId}`;
        } else if ( mapsId ) {
            return `/maps/${mapsId}`;
        } else if ( integrationAccessId ) {
            return `/integration/maps/${integrationAccessId}`;
        }
        return '/plans';
    }


    const addWMSLayer = (url) => {
        if(typeof url !== "undefined") {
            handelMapLoad(url);
        }
    };

    const handelSelectLayer= (e,key) => {

        var newLayerCategory = {...layerCategory};

        if(typeof key !== "undefined") {

            if(e === false) {
                newLayerCategory[key].visible = false;
                for (let l of newLayerCategory[key]["layerIds"]) {
                    map?.setLayoutProperty(l, 'visibility', 'none');
                }

                setLayerCategory(newLayerCategory);
                setShowAllLayer(null);
            } else {
                newLayerCategory[key].visible = true;
                for (let l of newLayerCategory[key]["layerIds"]) {
                    map?.setLayoutProperty(l, 'visibility', 'visible');
                }
                if (!map) {
                    setMap(map);
                }
                setLayerCategory(newLayerCategory);
                setShowAllLayer(null);
            }
        } else {
            if (e === false) {
                Object.keys(newLayerCategory).forEach((k) => {
                    newLayerCategory[k].visible = false;
                    for (let l of newLayerCategory[k]["layerIds"]) {
                        map?.setLayoutProperty(l, 'visibility', 'none');
                    }
                });
                setLayerCategory(newLayerCategory);
                setShowAllLayer(false);
            } else {
                Object.keys(newLayerCategory).forEach((k) => {
                    newLayerCategory[k].visible = true;
                    for (let l of newLayerCategory[k]["layerIds"]) {
                        map?.setLayoutProperty(l, 'visibility', 'visible');
                    }
                });
                if (!map) {
                    setMap(map);
                }
                setLayerCategory(newLayerCategory);
                setShowAllLayer(true);
            }
        }
    };

    const handelMapLoad = async (mapUrl) => {

        let isDrawing = false;
        const setIsDrawing = (value) => { 
            isDrawing = value;
        }

        // Create a custom control to toggle 3D Buildings
        const map = getMap(mapContainerRef);
        const draw = isReview ? new mapboxDraw({
                displayControlsDefault: false,
                defaultMode: 'simple_select',
                controls: {
                    point: true,
                }
            }) : null;
        const ruler = new RulerControl({
            labelFormat: n => `${(n*1000).toFixed(2)} m`,
          });

        // detect touches create order Button
        map.on('click', (e) => handleMapInteraction(e.point, map, draw, setCurrentPoint, setShowDetails, setOpenReviewDetails, setDetailsData, ruler));
        map.on('touchend', (e) => handleMapInteraction(e.point, map, draw, setCurrentPoint, setShowDetails, setOpenReviewDetails, setDetailsData, ruler));
        map.on('style.load', () => getMainStyle(map, plan, setMap, setLayerCategory));
        map.on('style.load', () => get3dStyle(map, review, mapUrl));

        // add Review Functionality
        if(isReview === true) {
            map.on('draw.create', (e) => handleDrawCreate(e, setCurrentPoint, setShowDetails, setOpenReviewCreate, draw, setIsDrawing));
            map.on('draw.update', () => handleDrawUpdate(setShowDetails, setOpenReviewCreate));
            map.on('draw.delete',() => handleDrawDelete(setOpenReviewDetails, setShowDetails, setOpenReviewCreate));
            map.on('draw.selectionchange', (e) => handleMapSelectChange(e));
            map.on('draw.modechange',(e) => handleDrawModeChange(e, draw, isDrawing, setIsDrawing));
            map.addControl(draw, 'top-right');
        } else if (isIntegration === false) { map.addControl(new ButtonPlanToOrder(() => setNewOrder(true)), "top-right");}

        map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        map.addControl(new ShowBaseMapPicker(() => setShowBaseMap(true)), "top-right");
        map.addControl(new ButtonLayerPicker(() => setShowLayer(true)), "top-right");
        map.addControl(new Button3D(() => toggle3d(map)), "top-right");
        map.addControl(ruler, 'top-right');

        map.addControl(new mapboxgl.ScaleControl(), 'bottom-right');

        setMap(map);
    }

    useEffect(() => {
        const getPlan = async () => {

            try {
                if (openDataPlanId ) {
                    // --------------plan is an integration plan ------------------------
                    const plan = await APIGetOpenDataPlan(openDataPlanId);
                    setPlanName(null);
                    setPlanId(null);
                    setPlan(plan);
                    setIsIntegration(true);
                    return;
                } else if (integrationAccessId && integrationPlanId) {
                    // --------------plan is an integration plan ------------------------
                    const {plan, planJson} = await APIGetPlanIntegration(integrationAccessId, integrationPlanId);
                    setPlanName(plan.name);
                    setPlanId(plan.planId);
                    setPlan(planJson);
                    setIsIntegration(true);
                    return;
                } else if (orderId && companyId && version && fileId && fileName && user) {
                    // --------------plan is an order review ------------------------

                    /*
                    if (user.companyId === companyId) {
                        setIsReviewCreator(true);
                    } else {
                        const contractor = await APIGetContractPartner({orderId, companyId});

                        if ( contractor && contractor.id === user.companyId ) {
                            setIsReviewCreator(false);
                        } else {
                           // navigate('/404');
                        }
                    }*/

                    const fileInfo = {orderId,companyId,version,fileId,fileName};
                    const [orderReview, {plan, planJson}] = await Promise.all([
                        APIGetOrderReviews({orderId, companyId: user.companyId}),
                        APIGetOrderPlan(fileInfo)
                    ]);

                    if(isReview === true) {
                        setReview(orderReview);
                    }

                    setPlanName(plan.name);
                    setPlanId(plan.planId);
                    setPlan(planJson);
                    setIsIntegration(false);
                    return;

                } else if (id) {
                    // -----------------plan is a plan -----------------------------
                    const {plan, planJson} = await APIGetPlan(id);
                    setPlanName(plan.name);
                    setPlanId(plan.planId);
                    setRefs(plan.refs);
                    setPlan(planJson);
                    setIsIntegration(false);
                    return;
                } else {
                    navigate('/404');
                }

            } catch (error) {
                console.error('Error reading file from S3:', error);
            }
        }

        if (plan === null) {
            getPlan();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    if (plan !== null && map === null) {
        handelMapLoad();
        return;
    }

    return (
        <div>
            {!isIntegration && user && (
                <NewOrderModal
                    openNewOrderModal={newOrder}
                    setOpenNewOrderModal={setNewOrder}
                    planId={id}
                />
            )}
            <div className={"text-base z-10 absolute m-5 ml-0 sm:ml-8 mt-6 sm:mt-8 mb-16 leading-7 text-gray-600"}>
                <div className="mt-2  md:flex md:items-center md:justify-between">
                    <div className="min-w-0 overflow-hidden flex-1" style={{width:"calc(100vw - 200px)"}}>
                        <div className={"flex"}>
                            {(isIntegration === false) && (
                                <Link to={getBackLink()} >
                                    <HiChevronLeft
                                        className={"text-3xl h-12 w-12 cursor-pointer mr-2"}
                                        style={{strokeWidth:"1px", fill:"#000", stroke:"#fff"}}
                                    />
                                </Link>
                            )}
                            <div>
                                <h2 className="text-2xl items-center textShadow flex font-normal leading-7 text-gray-900 truncate mr-12 " >{planName}</h2>
                                <p  className={"textShadow"}>{planId && `Plan-Nummer: ${planId}`}</p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <ViewerContext.Provider value={{id,map,setMap,addWMSLayer,setLegalShort, plan, refs,planName,handelSelectLayer,commentData,showBaseMap,setShowBaseMap, detailsData,layerCategory,setLayerCategory,showLayer, setShowLayer, showAllLayer, showDetails, setShowDetails, layerBlink, setLayerBlink}}>
                <div className="map-container z-0" ref={mapContainerRef}  style={{  width: "100%",  height: "calc(100vh - 0px )"}} >
                    {(map === null) && (
                        <div className='flex w-full h-dvh justify-center items-center'>
                            <LoadingIndicator/>
                        </div>
                    )}
                </div>
                {(legalShort) ? (
                    <div className={"absolute right-[280px] p-[2px] bg-white bottom-[0px] text-xs"}> {legalShort} </div>
                ) : ("")}

                <DetailsSideOver/>
                <BaseMapSideOver/>
                <LayersSideOver map={map}/>

            </ViewerContext.Provider>
            {(typeof fileName !== 'undefined' && typeof companyId !== 'undefined' && typeof version !== 'undefined') && (
                <ReviewContext.Provider value={{id: orderId,openReviewCreate,handelMapLoad, setMap, setOpenReviewCreate,review, setReview, openReviewDetails,setOpenReviewDetails, currentPoint,setCurrentPoint}}>
                <ReviewCreate/>
                <ReviewDetails/>
            </ReviewContext.Provider>
            )}
        </div>
    );
}
